import React from 'react';
import {
  Box, Button, FormControl, IconButton, InputAdornment, MenuItem, Select, TextField,
  Typography, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CustomDatePicker from '../form-components/CustomDatePicker';
import LocalText from '../common/LocalText';
import { useLocalization } from '../../context/LocalizationContext';
import { C, btnBase, ctaNavy } from '../../theme/adminTokens';

// Values must match ScoreboardFilterType on the backend (Enum.TryParse is case-insensitive,
// but the value itself has to be one of its member names) — 'alltime' maps to AllTime, which
// reads straight off the scoreboard table's total_score instead of aggregating sessions.
export const FILTERS = [
  { value: 'alltime', label: 'All Time' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom' },
];

export const SORT_OPTIONS = [
  { value: 'asc', label: 'Rank: Ascending' },
  { value: 'desc', label: 'Rank: Descending' },
];

// Same resolution order used everywhere else in the CMS a LocalizedPairs value is
// displayed (see HostingCard.jsx): current locale's value, else the first value present.
// Exported — GameAnnouncementSection's own, independent Game selector reuses this exact
// logic rather than a second copy, since it must resolve names identically.
export const resolveGameName = (game, locale) =>
  game?.localizedName?.values?.find((v) => v.localeId === locale)?.value
  ?? game?.localizedName?.values?.[0]?.value
  ?? game?.gameCode
  ?? '';

// Fixed height shared by the Game select and the Time Period toggle group — MUI's own
// "small" sizing for a Select vs. a ToggleButtonGroup doesn't naturally match, which is why
// their labels above them (and the controls themselves) previously sat at different heights
// when placed side by side.
const CONTROL_HEIGHT = 40;

// Same segmented-control treatment as DateRangeFilter's toggleGroupSx (FreeTrialAddItems'
// preset filter), reused here so every filter control in the app looks like one system.
const toggleGroupSx = {
  height: CONTROL_HEIGHT,
  alignItems: 'center',
  bgcolor: '#ffffff',
  border: `1px solid ${C.border}`,
  borderRadius: '10px',
  p: '3px',
  boxSizing: 'border-box',
  '& .MuiToggleButtonGroup-grouped': {
    border: 0,
    borderRadius: '8px !important',
    px: { xs: 1, sm: 2 },
    py: 0.5,
    fontSize: { xs: 11, sm: 12.5 },
    fontWeight: 700,
    textTransform: 'none',
    color: C.muted,
    // Without this, a squeezed flex row shrinks the button below its label's own width and
    // the label wraps mid-word ("All" / "Time") instead of the button just staying its
    // natural width and the row wrapping around it.
    whiteSpace: 'nowrap',
    '&:hover': { bgcolor: '#eff6ff' },
    '&.Mui-selected': { ...ctaNavy },
  },
};

// One shared label style for every control in this bar (Game / Time Period Filter / From /
// To) — previously "Game" was plain-case while "Time Period Filter" was uppercase with
// letter-spacing, so the two sat side by side looking like two different design languages.
// Exported alongside resolveGameName — same reuse reasoning.
export const filterLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  mb: 0.5,
};

export const selectSx = {
  minWidth: { xs: '100%', sm: 220 },
  height: CONTROL_HEIGHT,
  bgcolor: '#ffffff',
  borderRadius: '10px',
  fontSize: 13,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.navy },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.navy },
};

const searchFieldSx = {
  minWidth: { xs: '100%', sm: 260 },
  bgcolor: '#ffffff',
  borderRadius: '10px',
  '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: 13 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.navy },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.navy },
};

const sortSelectSx = {
  minWidth: { xs: 140, sm: 180 },
  width: '100%',
  bgcolor: '#ffffff',
  borderRadius: '10px',
  fontSize: 13,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.navy },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.navy },
};

const refreshBtnSx = {
  border: `1px solid ${C.border}`,
  borderRadius: '10px',
  bgcolor: '#ffffff',
  color: C.navy,
  '&:hover': { bgcolor: '#eff6ff', borderColor: C.navy },
};

/**
 * Game selector + Weekly/Monthly/Custom segmented filter for the scoreboard section.
 * Custom reveals two date inputs and an Apply button; the parent only refetches when
 * Apply is clicked (draft dates live here so partial entry never triggers a fetch).
 */
export default function ScoreboardFilterBar({
  games,
  gameId,
  onGameChange,
  filter,
  onFilterChange,
  draftFrom,
  draftTo,
  onDraftFromChange,
  onDraftToChange,
  onApplyCustom,
  search,
  onSearchChange,
  sortDirection,
  onSortDirectionChange,
  onRefresh,
  disabled = false,
}) {
  const { locale } = useLocalization();
  const rangeError = Boolean(draftFrom && draftTo && draftFrom > draftTo);
  const canApply = filter === 'custom' && Boolean(draftFrom && draftTo) && !rangeError;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5 }}>
        <TextField
          size="small"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search player or email..."
          // Never disabled by the table's own loading state (unlike the other controls
          // here) — same convention as TransactionTable.jsx's search field. Disabling it
          // mid-fetch would blur a focused input and silently drop further keystrokes if
          // the user kept typing while a debounced request was still in flight.
          sx={searchFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: C.muted }} />
              </InputAdornment>
            ),
          }}
          inputProps={{ 'aria-label': 'Search player or email' }}
        />

        <Box>
          <Typography id="scoreboard-game-label" sx={filterLabelSx}>
            <LocalText>Game</LocalText>
          </Typography>
          <FormControl size="small" disabled={disabled || games.length === 0}>
            <Select
              value={gameId ?? ''}
              onChange={(e) => onGameChange(e.target.value)}
              displayEmpty
              sx={selectSx}
              labelId="scoreboard-game-label"
              inputProps={{ 'aria-label': 'Select a game' }}
              renderValue={(v) => {
                if (!v) return <Typography sx={{ color: '#94a3b8', fontSize: 13 }}><LocalText>Select a game</LocalText></Typography>;
                const g = games.find((game) => game.id === v);
                return <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{resolveGameName(g, locale)}</Typography>;
              }}
            >
              {games.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {resolveGameName(g, locale)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography sx={filterLabelSx}>
            <LocalText>Time Period Filter</LocalText>
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filter}
            onChange={(_e, next) => { if (next !== null) onFilterChange(next); }}
            disabled={disabled}
            sx={toggleGroupSx}
            aria-label="scoreboard time period"
          >
            {FILTERS.map((f) => (
              <ToggleButton key={f.value} value={f.value} aria-label={f.label}>
                <LocalText>{f.label}</LocalText>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {filter === 'custom' && (
          <>
            <Box sx={{ width: { xs: 'calc(50% - 6px)', sm: 160 } }}>
              <Typography sx={filterLabelSx}>
                <LocalText>From</LocalText>
              </Typography>
              <CustomDatePicker
                value={draftFrom}
                max={draftTo || undefined}
                onChange={(e) => onDraftFromChange(e.target.value)}
                placeholder="Start date"
                enableYearNav
                fullWidth
              />
            </Box>
            <Box sx={{ width: { xs: 'calc(50% - 6px)', sm: 160 } }}>
              <Typography sx={{ ...filterLabelSx, color: rangeError ? '#ef4444' : C.muted }}>
                <LocalText>To</LocalText>
              </Typography>
              <CustomDatePicker
                value={draftTo}
                min={draftFrom || undefined}
                onChange={(e) => onDraftToChange(e.target.value)}
                placeholder="End date"
                isError={rangeError}
                enableYearNav
                fullWidth
              />
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={onApplyCustom}
              disabled={disabled || !canApply}
              sx={{ ...btnBase, ...ctaNavy, height: 36, width: { xs: '100%', sm: 'auto' } }}
            >
              <LocalText>Apply</LocalText>
            </Button>
          </>
        )}

        {/* Pushed to the far right of this same row when there's room; wraps onto its own
            full-width line below everything else once the row runs out of space. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { xs: 0, sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}>
          <FormControl size="small" disabled={disabled} sx={{ flex: { xs: 1, sm: 'initial' } }}>
            <Select
              value={sortDirection}
              onChange={(e) => onSortDirectionChange(e.target.value)}
              sx={sortSelectSx}
              inputProps={{ 'aria-label': 'Sort order' }}
            >
              {SORT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <LocalText>{opt.label}</LocalText>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            onClick={onRefresh}
            disabled={disabled}
            sx={refreshBtnSx}
            aria-label="Refresh scoreboard"
          >
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {filter === 'custom' && rangeError && (
        <Typography sx={{ fontSize: 11, color: '#ef4444' }}>
          <LocalText>Start date must be on or before the end date.</LocalText>
        </Typography>
      )}

      {/* The table keeps showing the last-applied results while a custom range is being
          entered — this makes that explicit instead of leaving the mismatch unexplained. */}
      {filter === 'custom' && !rangeError && (
        <Typography sx={{ fontSize: 11, color: C.muted }}>
          <LocalText>Choose a date range and click Apply to update the table below.</LocalText>
        </Typography>
      )}
    </Box>
  );
}
